import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import { documentsApi, isCancelled } from './api';
import { EXPORT_FORMATS, WATCH_COLORS } from './constants';
import { apiBlobError, apiError, daysLabel, downloadBlob, fmtDateTime } from './format';
import { StatusChip, WatchBadge } from './DocumentsTable';
import { ExportFormat, WarningDocument, WarningsResponse, WatchAction, WatchKind } from './types';
import { notify } from '../../notifications';

interface Props {
  /** Версия данных: при изменении список перезагружается (например, после продления из карточки) */
  refreshKey: number;
  onOpen: (id: number) => void;
  onChanged: () => void;
}

type KindFilter = 'all' | WatchKind;
type ActionFilter = 'all' | WatchAction;

const KIND_LABELS: Record<WatchKind, string> = { direct: 'Прямое', via: 'Через РЦ', return: 'Возврат' };

const BORDER = '#e2e8f0';

const WarningsPanel: React.FC<Props> = ({ refreshKey, onOpen, onChanged }) => {
  const [data, setData] = useState<WarningsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<KindFilter>('all');
  const [action, setAction] = useState<ActionFilter>('all');
  const [onlyCritical, setOnlyCritical] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [confirmIds, setConfirmIds] = useState<number[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [exportMenu, setExportMenu] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState(false);
  // открытие вкладки записано в журнал действий: обновления и перезагрузка после продления идут с silent
  const audited = useRef(false);
  const seq = useRef(0);

  const load = useCallback((signal?: AbortSignal) => {
    const current = ++seq.current;
    const silent = audited.current;
    setLoading(true);
    return documentsApi.warnings(silent, signal)
      .then(res => {
        if (current !== seq.current) return;
        audited.current = true;
        setData(res);
        setError(null);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        setError(apiError(err, 'Не удалось загрузить проблемные документы'));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    setSelected([]);
    return () => controller.abort();
  }, [load, refreshKey]);

  const runExport = async (format: ExportFormat) => {
    setExportMenu(null);
    setExporting(true);
    try {
      const file = await documentsApi.exportWarnings(format);
      downloadBlob(file.filename, file.blob);
    } catch (err) {
      notify.error(await apiBlobError(err, 'Не удалось выгрузить проблемные документы'));
    } finally {
      setExporting(false);
    }
  };

  const rows = useMemo<WarningDocument[]>(() => {
    if (!data) return [];
    return data.documents.filter(d =>
      (kind === 'all' || d.watch?.kind === kind)
      && (action === 'all' || d.watch?.action === action)
      && (!onlyCritical || d.watch?.level === 'critical')
    );
  }, [data, kind, action, onlyCritical]);

  const canExtend = Boolean(data?.canExtend);
  const visibleIds = rows.map(r => r.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.includes(id));

  const toggleAll = () => setSelected(allSelected ? selected.filter(id => !visibleIds.includes(id)) : Array.from(new Set([...selected, ...visibleIds])));
  const toggleOne = (id: number) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const runExtend = async () => {
    if (!confirmIds) return;
    setBusy(true);
    try {
      const res = await documentsApi.extend(confirmIds);
      notify.success(res.message || `Продлено: ${res.extended}`);
      setConfirmIds(null);
      setSelected([]);
      onChanged();
      await load();
    } catch (err) {
      notify.error(apiError(err, 'Не удалось продлить срок'));
      setConfirmIds(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {data && (
        <Typography variant="body2" color="text.secondary">
          Автоочистка считает дни без изменений: прямые перемещения - {daysLabel(data.rules.direct.cancelAfter)},
          через РЦ - {daysLabel(data.rules.via.cancelAfter)}, возвраты - {daysLabel(data.rules.return.cancelAfter)}.
          Документы «в пути», «в обработке», «подтвердить отправку» и «ожидает подтверждения» по истечении срока отменяются,
          черновики и документы с ошибками - скрываются. Оранжевый бейдж - срок близко, красный - срок истёк.
          {!canExtend && ' Ваша роль работает с вкладкой в режиме просмотра - продлевать срок нельзя.'}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <ToggleButtonGroup
          size="small" exclusive value={kind} onChange={(_, v) => v && setKind(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, borderColor: BORDER } }}
        >
          <ToggleButton value="all">Все{data ? ` · ${data.summary.total}` : ''}</ToggleButton>
          <ToggleButton value="direct">Прямые{data ? ` · ${data.summary.direct}` : ''}</ToggleButton>
          <ToggleButton value="via">Через РЦ{data ? ` · ${data.summary.via}` : ''}</ToggleButton>
          <ToggleButton value="return">Возвраты{data ? ` · ${data.summary.returns}` : ''}</ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup
          size="small" exclusive value={action} onChange={(_, v) => v && setAction(v)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, borderColor: BORDER } }}
        >
          <ToggleButton value="all">Любое действие</ToggleButton>
          <ToggleButton value="cancel">Отмена{data ? ` · ${data.summary.cancel}` : ''}</ToggleButton>
          <ToggleButton value="hide">Скрытие{data ? ` · ${data.summary.hide}` : ''}</ToggleButton>
        </ToggleButtonGroup>
        <Chip
          clickable size="small" onClick={() => setOnlyCritical(v => !v)}
          label={`Срок истёк${data ? ` · ${data.summary.critical}` : ''}`}
          variant={onlyCritical ? 'filled' : 'outlined'}
          sx={{
            height: 30, fontWeight: 600, borderColor: onlyCritical ? WATCH_COLORS.critical : BORDER,
            bgcolor: onlyCritical ? WATCH_COLORS.critical : '#fff', color: onlyCritical ? '#fff' : '#334155',
            '&:hover': { bgcolor: onlyCritical ? WATCH_COLORS.critical : '#fef2f2' }
          }}
        />
        <Box sx={{ flex: 1 }} />
        {data?.canExport && (
          <>
            <Button
              size="small" variant="outlined" color="inherit" disabled={exporting || !data.summary.total}
              onClick={e => setExportMenu(e.currentTarget)}
              startIcon={exporting ? <CircularProgress size={14} /> : <FileDownloadOutlinedIcon fontSize="small" />}
              sx={{ textTransform: 'none', borderColor: BORDER, height: 32 }}
            >
              {exporting ? 'Формирование…' : 'Выгрузить'}
            </Button>
            <Menu anchorEl={exportMenu} open={Boolean(exportMenu)} onClose={() => setExportMenu(null)}>
              {EXPORT_FORMATS.map(f => (
                <MenuItem key={f.value} onClick={() => runExport(f.value)} sx={{ fontSize: 14 }}>{f.label}</MenuItem>
              ))}
            </Menu>
          </>
        )}
        {canExtend && (
          <Button
            size="small" variant="contained" disableElevation startIcon={<MoreTimeRoundedIcon />}
            disabled={!selected.length} onClick={() => setConfirmIds(selected)} sx={{ textTransform: 'none' }}
          >
            Продлить выбранные{selected.length ? ` (${selected.length})` : ''}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => load()} sx={{ textTransform: 'none' }}>Повторить</Button>}>
          {error}
        </Alert>
      )}
      {data && data.shown < data.summary.total && (
        <Typography variant="caption" color="text.secondary">
          На экране {data.shown.toLocaleString('ru-RU')} самых давних документов из {data.summary.total.toLocaleString('ru-RU')}.
          {data.canExport ? ' Полный список - в выгрузке.' : ''}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 340px)', minHeight: 200, opacity: loading && data ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 12, color: '#475569', borderBottom: `1px solid ${BORDER}` } }}>
                {canExtend && (
                  <TableCell padding="checkbox">
                    <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && visibleIds.some(id => selected.includes(id))} onChange={toggleAll} disabled={!rows.length} />
                  </TableCell>
                )}
                <TableCell>№</TableCell>
                <TableCell>Тип</TableCell>
                <TableCell>Откуда → Куда</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Обновлён</TableCell>
                <TableCell align="right">Без изменений</TableCell>
                <TableCell>Срок</TableCell>
                {canExtend && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(d => (
                <TableRow
                  key={d.id} hover onClick={() => onOpen(d.id)} selected={selected.includes(d.id)}
                  sx={{ cursor: 'pointer', '& td': { py: 0.9, borderBottom: '1px solid #f1f5f9' } }}
                >
                  {canExtend && (
                    <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.includes(d.id)} onChange={() => toggleOne(d.id)} />
                    </TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 700 }}>{d.id}</TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{d.watch ? KIND_LABELS[d.watch.kind] : d.typeName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.sender || '-'}</Typography>
                      <ArrowForwardRoundedIcon sx={{ fontSize: 15, color: '#94a3b8' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.recipient || '-'}</Typography>
                    </Box>
                    {d.route === 'via' && <Typography variant="caption" color="text.secondary">через {d.via || 'РЦ'}</Typography>}
                  </TableCell>
                  <TableCell><StatusChip status={d.status} name={d.statusName} /></TableCell>
                  <TableCell sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>{fmtDateTime(d.updatedAt)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 13 }}>{d.watch ? daysLabel(d.watch.idleDays) : '-'}</TableCell>
                  <TableCell><WatchBadge watch={d.watch} /></TableCell>
                  {canExtend && (
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <Tooltip title="Продлить срок">
                        <Button size="small" onClick={() => setConfirmIds([d.id])} sx={{ textTransform: 'none', minWidth: 0 }}>Продлить</Button>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={canExtend ? 9 : 7} align="center" sx={{ py: 6, color: '#64748b' }}>
                    {data && data.documents.length ? 'По выбранным условиям ничего нет' : 'Проблемных документов нет'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={confirmIds !== null} onClose={() => !busy && setConfirmIds(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff', borderBottom: `1px solid ${BORDER}`, fontSize: 16, fontWeight: 700 }}>Продление срока</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2">
            {confirmIds && confirmIds.length === 1
              ? `Срок документа №${confirmIds[0]} будет продлён, счётчик простоя обнулится.`
              : `Срок ${confirmIds?.length || 0} документов будет продлён, счётчик простоя обнулится.`}{' '}
            Продолжить?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button color="inherit" onClick={() => setConfirmIds(null)} disabled={busy}>Отмена</Button>
          <Button variant="contained" disableElevation onClick={runExtend} disabled={busy} startIcon={busy ? <CircularProgress size={16} /> : undefined}>
            Подтвердить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WarningsPanel;
